/**
 * CustomerContextPanel — "what is this conversation's run actually bound to"
 * (DIGEMP-CUSTOMER-CONTEXT-BINDING-FRONTEND-065).
 *
 * Read-only, and every verdict on it comes from one author: GET /api/agent-runs/:id/context-binding
 * (app/services/context_binding.py) — the aggregate state and its Hebrew wording, each binding's
 * fail-closed status, the entries to look at, the customer boundary and every explanatory sentence.
 * The panel derives exactly one thing itself: which run to ask about (the newest ConversationMessage
 * carrying an agent_run_id, the only conversation→run link the schema defines).
 *
 * It deliberately holds NO binding rules of its own. The version before this one re-derived the
 * same facts from the ticket/company/run payloads with its own complete/missing/conflict vocabulary
 * and could contradict the projection — it showed the all-verified badge above its own
 * device-mismatch note on a run the server reports as a conflict, and printed a system-generated
 * ticket's system@prodigy.internal sender as the requesting user. When the projection cannot be
 * read the panel now says so instead of falling back to a guess.
 *
 * Only the display fields each kind is allowed to render are read (see VALUE_RENDERERS); a kind this
 * build does not recognise renders its label and status and never its value, so a field added to the
 * server's `value` dict cannot dump raw payload here. run.context, analysis text and message content
 * are never read at all.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agentRunsApi, conversationsApi } from "../services/api";
import { bindingStateMeta, bindingStatusMeta } from "./contextBindingVocabulary";
import { statusChipMeta } from "./statusVocabulary";
import { actionTypeLabel } from "./actionTypeVocabulary";

function txt(value, max = 160) {
  return typeof value === "string" && value.trim() ? value.trim().slice(0, max) : null;
}

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("he-IL"); } catch { return ""; }
}

function Ltr({ children }) {
  return <span dir="ltr" className="font-mono">{children}</span>;
}

function Sep() {
  return <span className="text-gray-400"> · </span>;
}

// ── one renderer per binding kind, reading only the fields that kind may display ──
function EmployeeValue({ value }) {
  const name = txt(value.name, 120);
  if (!name) return null;
  const role = txt(value.role_title, 120);
  return (
    <>
      {name}
      {role ? <><Sep /><span className="text-xs text-gray-500">{role}</span></> : null}
      {value.is_enabled === false ? (
        <><Sep /><span className="text-xs text-gray-500">{statusChipMeta("disabled").label}</span></>
      ) : null}
    </>
  );
}

function TicketValue({ value }) {
  const title = txt(value.title, 120);
  if (!value.ticket_number && !title) return null;
  const label = `${value.ticket_number ? `#${value.ticket_number}` : "קריאה מקושרת"}${title ? ` · ${title}` : ""}`;
  const ticketId = txt(value.ticket_id, 60);
  return (
    <>
      {ticketId ? (
        <Link to={`/tickets/${ticketId}`} className="text-blue-600 hover:underline">{label}</Link>
      ) : label}
      {value.system_generated ? (
        <><Sep /><span className="text-xs text-gray-500">נוצרה על ידי המערכת</span></>
      ) : null}
    </>
  );
}

function CustomerValue({ value }) {
  const name = txt(value.name, 120);
  if (name) return name;
  const onTicket = txt(value.ticket_customer_name, 120);
  const onConversation = txt(value.conversation_customer_name, 120);
  if (!onTicket && !onConversation) return null;
  return (
    <>
      <span>בקריאה: {onTicket || "ללא שם"}</span>
      <Sep />
      <span>בשיחה: {onConversation || "ללא שם"}</span>
    </>
  );
}

function PersonValue({ value }) {
  const name = txt(value.name ?? value.display_name, 120);
  const email = txt(value.email, 120);
  if (!name && !email) return null;
  return (
    <>
      {name}
      {email ? (
        <span className="text-xs text-gray-500" dir="ltr">{name ? " · " : ""}{email}</span>
      ) : null}
    </>
  );
}

function UserValue({ value }) {
  // A system-generated ticket's entry says so in its own detail; it has no requesting user to name.
  if (value.system_generated) return null;
  return <PersonValue value={value} />;
}

function DeviceValue({ value }) {
  const runDevice = txt(value.run_context_device, 120);
  const conversationDevice = txt(value.conversation_device, 120);
  if (runDevice && conversationDevice) {
    return (
      <>
        <span>בהרצה: <Ltr>{runDevice}</Ltr></span>
        <Sep />
        <span>בשיחה: <Ltr>{conversationDevice}</Ltr></span>
      </>
    );
  }
  const identifier = txt(value.identifier, 120);
  return identifier ? <Ltr>{identifier}</Ltr> : null;
}

function TargetValue({ value }) {
  const target = txt(value.target, 160);
  if (!target) return null;
  const actionType = txt(value.action_type, 60);
  const typeLabel = actionTypeLabel(actionType);
  return (
    <>
      <Ltr>{target}</Ltr>
      {typeLabel ? <><Sep /><span className="text-xs text-gray-500">{typeLabel}</span></> : null}
    </>
  );
}

function ConversationValue({ value }) {
  const status = txt(value.status, 40);
  return status ? <Ltr>{status}</Ltr> : null;
}

const VALUE_RENDERERS = {
  employee: EmployeeValue,
  ticket: TicketValue,
  customer: CustomerValue,
  user: UserValue,
  operator: PersonValue,
  device: DeviceValue,
  target: TargetValue,
  conversation: ConversationValue,
};

const DETAIL_CLS = {
  conflict: "text-red-700",
  missing: "text-amber-700",
};

function BindingRow({ entry }) {
  const status = bindingStatusMeta(entry.status);
  const Renderer = VALUE_RENDERERS[entry.kind];
  const value = entry.value && typeof entry.value === "object" ? entry.value : null;
  const sources = Array.isArray(entry.sources) ? entry.sources.filter((s) => typeof s === "string" && s.trim()) : [];
  const detail = txt(entry.detail, 300);

  return (
    <div data-testid={`ccb-row-${entry.kind}`} className="flex items-start gap-2 text-sm">
      <span className="text-gray-500 shrink-0 w-20">{txt(entry.label, 40) || entry.kind}</span>
      <span className="text-gray-800 min-w-0 break-words">
        {Renderer && value ? <Renderer value={value} /> : null}
        <span className={`ms-2 text-[10px] px-1.5 py-0.5 rounded-full border ${status.cls}`}>
          {status.label}
        </span>
        {detail && (
          <div className={`text-[11px] mt-0.5 ${DETAIL_CLS[entry.status] || "text-gray-500"}`}>{detail}</div>
        )}
        {sources.length > 0 && (
          <div dir="ltr" className="text-[10px] text-gray-400 font-mono break-all">{sources.join(" · ")}</div>
        )}
      </span>
    </div>
  );
}

export default function CustomerContextPanel({ conversation }) {
  const conversationId = conversation?.id || null;
  const declaresContext = Boolean(conversation?.ticket_id || conversation?.customer_id);

  const messagesQuery = useQuery({
    queryKey: ["customer-context-messages", conversationId],
    queryFn: () => conversationsApi.listMessages(conversationId),
    enabled: Boolean(conversationId),
    retry: false,
  });

  // listMessages is ordered by sequence_no, so the last message carrying a run id is the newest task.
  const latestRunId = useMemo(() => {
    const messages = Array.isArray(messagesQuery.data) ? messagesQuery.data : [];
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (txt(messages[i]?.agent_run_id, 80)) return messages[i].agent_run_id;
    }
    return null;
  }, [messagesQuery.data]);

  const bindingQuery = useQuery({
    queryKey: ["customer-context-binding", latestRunId],
    queryFn: () => agentRunsApi.contextBinding(latestRunId),
    enabled: Boolean(latestRunId),
    retry: false,
  });

  const binding = bindingQuery.data && typeof bindingQuery.data === "object" ? bindingQuery.data : null;
  const entries = Array.isArray(binding?.bindings) ? binding.bindings.filter((e) => e && typeof e === "object") : [];
  const messagesFailed = Boolean(conversationId) && messagesQuery.isError;
  const projectionFailed = Boolean(latestRunId) && bindingQuery.isError;
  const loading = (Boolean(conversationId) && messagesQuery.isLoading)
    || (Boolean(latestRunId) && bindingQuery.isLoading);

  // The projection is the only author, so the arms are exhaustive: either it answered and it is
  // rendered verbatim, or the panel says it could not be read — never a locally derived verdict.
  const arm = binding ? "projection"
    : projectionFailed || messagesFailed ? "unavailable"
      : loading ? "loading"
        : "norun";

  const meta = arm === "projection"
    ? bindingStateMeta(binding)
    : arm === "loading"
      ? { label: "בודק שיוך…", cls: "bg-blue-50 text-blue-700 border-blue-200" }
      : arm === "unavailable"
        ? { label: "לא ניתן לאמת", cls: "bg-gray-200 text-gray-600 border-gray-300" }
        : { label: "טרם אומת", cls: "bg-gray-200 text-gray-600 border-gray-300" };

  const attentionKinds = arm === "projection" && Array.isArray(binding.attention) ? binding.attention : [];
  const attentionLabels = attentionKinds
    .map((kind) => txt(entries.find((e) => e.kind === kind)?.label, 40))
    .filter(Boolean);
  const scopeInScope = binding?.customer_scope ? binding.customer_scope.in_scope : null;
  const hasScopeVerdict = binding?.customer_scope && typeof binding.customer_scope === "object";

  return (
    <section
      data-testid="customer-context-panel"
      aria-labelledby="customer-context-title"
      className="bg-white rounded-xl shadow-sm border p-4 space-y-3"
      dir="rtl"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="customer-context-title" className="text-sm font-semibold text-gray-700">הקשר לקוח</h2>
        <span data-testid="ccb-status" className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${meta.cls}`}>
          {meta.label}
        </span>
      </div>
      <p className="text-[11px] text-gray-400">
        השיוך של הריצה האחרונה בשיחה — עובד דיגיטלי, קריאה, לקוח, משתמש מבקש, מכשיר ויעד — כפי שהשרת
        מאמת אותו מול הרשומות. הפאנל מציג את השיוך המאומת ואינו מסיק אותו בעצמו.
      </p>

      {arm === "norun" && (
        <div role="status" className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">
          {declaresContext
            ? "טרם הורצה משימה בשיחה זו — אין ריצה מאומתת להציג את השיוך שלה."
            : "לשיחה זו אין קריאה או לקוח משויכים — היא מתנהלת ישירות עם העובד, ללא הקשר לקוח. "
              + "יש לוודא שהמשימה אינה מחייבת שיוך ללקוח או לקריאה."}
        </div>
      )}
      {arm === "unavailable" && (
        <div role="status" className="text-xs bg-gray-50 border border-gray-200 text-gray-600 rounded px-3 py-2">
          {messagesFailed
            ? "לא ניתן לאמת את פרטי ההקשר — לא הצלחנו לקרוא את הודעות השיחה כדי לאתר את הריצה האחרונה. "
            : "לא ניתן לאמת את פרטי ההקשר — לא הצלחנו לקרוא את פירוט השיוך של הריצה מהשרת. "}
          הפאנל אינו מסיק את השיוך בעצמו, ולכן אינו מציג כאן שיוך שאינו מאומת.
        </div>
      )}
      {arm === "loading" && (
        <p className="text-xs text-gray-400">טוען את פרטי השיוך…</p>
      )}

      {arm === "projection" && attentionLabels.length > 0 && (
        <div
          role={binding.binding_state === "conflict" ? "alert" : "status"}
          className={`text-xs rounded px-3 py-2 border ${
            binding.binding_state === "conflict"
              ? "bg-red-50 border-red-200 text-red-800"
              : "bg-amber-50 border-amber-200 text-amber-800"
          }`}
        >
          דורש בירור: {attentionLabels.join(" · ")} — ראו את הפירוט בשורות המסומנות למטה.
        </div>
      )}

      {arm === "projection" && hasScopeVerdict && scopeInScope === false && (
        <div role="status" className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">
          העובד הדיגיטלי מוגבל ללקוח שאינו הלקוח של הקריאה — לפי אותו כלל שהמערכת מיישמת בעת יצירת ריצה,
          ריצה חדשה לקריאה זו תידחה.
        </div>
      )}
      {arm === "projection" && hasScopeVerdict && scopeInScope === null && (
        <div role="status" className="text-xs bg-gray-50 border border-gray-200 text-gray-600 rounded px-3 py-2">
          לא ניתן לאמת שהעובד הדיגיטלי פועל במסגרת הלקוח של הקריאה — פרטי העובד או הקריאה חסרים.
        </div>
      )}

      {entries.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
          {entries.map((entry, index) => (
            <BindingRow key={`${txt(entry.kind, 40) || "kind"}-${index}`} entry={entry} />
          ))}
        </div>
      )}

      {(conversation?.created_by || conversation?.created_at) && (
        <p className="text-[11px] text-gray-400 border-t border-gray-100 pt-2">
          נפתחה על ידי <span dir="ltr">{txt(conversation.created_by, 120) || "—"}</span>
          {conversation.created_at ? ` · ${fmt(conversation.created_at)}` : ""}
        </p>
      )}
    </section>
  );
}
